import { create } from 'zustand';

export const useStore = create((set, get) => ({
  // ── Navigation ──
  // 'projects' → project list  |  'project' → inside a project  |
  // 'admin' → admin console (users + future sections)
  view: 'projects',
  setView: (view) => set({ view }),
  // active page within a project
  projectPage: 'home', // 'home' | 'drawings' | 'files' | 'forms' | 'photos' | 'members' | 'settings'
  setProjectPage: (page) => set({ projectPage: page }),
  // active section within the admin console (mirrors projectPage's
  // pattern; 'users' is the only section today, more land here as the
  // org-level surface grows).
  adminPage: 'users', // 'users' | (future: audit, integrations, etc.)
  setAdminPage: (page) => set({ adminPage: page }),

  // Collapsed state for the project-level left nav. Persisted so iPad /
  // small-screen users who collapse for plan-viewing room stay collapsed
  // across reloads.
  navCollapsed: typeof window !== 'undefined' && localStorage.getItem('cf.navCollapsed') === '1',
  setNavCollapsed: (v) => {
    if (typeof window !== 'undefined') {
      if (v) localStorage.setItem('cf.navCollapsed', '1');
      else   localStorage.removeItem('cf.navCollapsed');
    }
    set({ navCollapsed: Boolean(v) });
  },
  toggleNavCollapsed: () => {
    const next = !get().navCollapsed;
    if (typeof window !== 'undefined') {
      if (next) localStorage.setItem('cf.navCollapsed', '1');
      else      localStorage.removeItem('cf.navCollapsed');
    }
    set({ navCollapsed: next });
  },

  // ── Current user (no auth yet, switched via UI) ──
  currentUser: null,
  setCurrentUser: (u) => set({ currentUser: u, role: u?.role || 'field' }),
  users: [],
  setUsers: (users) => set({ users }),

  // ── Sign-in gate ──
  // Source of truth is the JWT in localStorage (`cf.authToken`). On boot,
  // we mirror "token present" → signedIn true so a returning user lands
  // straight in the app instead of flashing the sign-in screen. The
  // bootstrap effect in App.jsx then validates the token via /auth/me;
  // if it's invalid, axios's 401 handler clears it and reloads.
  //
  // Legacy: pre-JWT builds set `cf.signedIn=1` directly. Strip it on
  // boot so a stale value doesn't bypass the new auth flow.
  signedIn: (() => {
    if (typeof window === 'undefined') return false;
    try { localStorage.removeItem('cf.signedIn'); } catch (_) {}
    return !!localStorage.getItem('cf.authToken');
  })(),
  setSignedIn: (v) => {
    set({ signedIn: Boolean(v) });
  },

  // ── Projects list ──
  projects: [],
  setProjects: (projects) => set({ projects }),

  // ── Project templates ──
  projectTemplates: [],
  setProjectTemplates: (t) => set({ projectTemplates: t }),

  // ── Active project ──
  // Setting the active project also wipes every per-project field below
  // so filters, selections, open modals, the active sheet, etc. don't
  // leak from one project into the next. Each project is its own
  // environment — switching jobs from the top dropdown should feel
  // like opening a fresh view, not inheriting the previous job's state.
  // Cross-project / org-wide state (currentUser, projects, navCollapsed,
  // markupClipboard, activeTool / colors as user prefs) is left alone.
  project: null,
  setProject: (project) => set((s) => {
    if (s.project?.id === project?.id) return { project };
    return {
      project,
      // Per-project role: clamp to the most restrictive role until
      // App.jsx refreshes from the server's `current_user_role`. Without
      // this, the previous project's role briefly leaks into the new
      // project (e.g. admin-in-A momentarily granting admin UI in B).
      role: 'field',
      // Filters + search
      filterDiscipline: '',
      filterVersionSet: '',
      searchQuery: '',
      // Sheet selection + viewer
      selectedSheets: new Set(),
      activeSheet: null,
      recentSheetIds: [],
      drawingsViewMode: 'list',
      sidebarTab: 'sheets',
      markups: [],
      markupClipboard: [],
      // Per-sheet viewer state (zoom/pan are sheet-scoped — reset so a
      // freshly-opened sheet in the new project starts at fit-to-window)
      zoom: 1.0,
      pan: { x: 0, y: 0 },
      // Lists that get refetched on project change — clear so the old
      // project's rows don't flash before the new fetch lands.
      sheets: [],
      disciplines: [],
      versionSets: [],
      // Markup tool flow state
      activeTool: 'pan',
      calibrationPickPoints: [],
      calibrationModalOpen: false,
      // Modals / panels — close anything left open in the previous project
      versionsPanelOpen: false,
      uploadModalOpen: false,
      resumeDraftVersionSetId: null,
      compareSetupOpen: false,
      compareConfig: null,
      contentSearchOpen: false,
      drawingsSettingsOpen: false,
      versionSetsModalOpen: false,
      versionSetsModalTab: null,
    };
  }),

  // ── Role ──
  // The caller's effective role for the active context.
  //  - On the projects list / admin console: tracks the org-wide users.role.
  //  - Inside a specific project: tracks the per-project members.role
  //    (server returns it as `current_user_role` on GET /api/projects/:id).
  // Always one of admin | pm | super | field. Components read `role`
  // unconditionally and don't need to know which context they're in.
  role: 'admin',
  setRole: (role) => set({ role }),

  // ── Disciplines ──
  disciplines: [],
  setDisciplines: (disciplines) => set({ disciplines }),

  // ── Version Sets ──
  versionSets: [],
  setVersionSets: (versionSets) => set({ versionSets }),

  // ── Sheets ──
  sheets: [],
  setSheets: (sheets) => set({ sheets }),
  activeSheet: null,
  // Recently viewed sheet ids (MRU, deduped, capped at 5). Maintained by
  // setActiveSheet so arrow-key nav, recents-dropdown clicks, and direct
  // opens all feed the same list.
  recentSheetIds: [],
  setActiveSheet: (sheet) => set((s) => {
    const base = {
      activeSheet: sheet,
      markups: [],
      activeTool: 'pan',
      calibrationPickPoints: [],
    };
    if (!sheet) return base;
    const prev = s.recentSheetIds || [];
    const next = [sheet.id, ...prev.filter((id) => id !== sheet.id)].slice(0, 5);
    return { ...base, recentSheetIds: next };
  }),
  // Drawings page view mode — 'list' shows the sheet table, 'viewer' opens
  // the PDF viewer. Lifted from local state in ProjectDrawings so the
  // sidebar nav can force a return to the list by calling the setter.
  drawingsViewMode: 'list',
  setDrawingsViewMode: (m) => set({ drawingsViewMode: m }),
  // Patch the active sheet's calibration locally AND mirror it into the sheets
  // list so downstream consumers (drawings list, etc.) stay in sync without a
  // full refetch.
  setActiveSheetCalibration: (calibration) => set((s) => {
    if (!s.activeSheet) return {};
    const updated = { ...s.activeSheet, calibration };
    return {
      activeSheet: updated,
      sheets: s.sheets.map((x) => x.id === updated.id ? updated : x),
    };
  }),

  // ── Filters ──
  filterDiscipline: '',
  filterVersionSet: '',
  searchQuery: '',
  setFilterDiscipline: (v) => set({ filterDiscipline: v }),
  setFilterVersionSet: (v) => set({ filterVersionSet: v }),
  setSearchQuery: (v) => set({ searchQuery: v }),

  // ── Sidebar tab ──
  sidebarTab: 'sheets', // 'sheets' | 'markups'
  setSidebarTab: (t) => set({ sidebarTab: t }),

  // ── Panels ──
  versionsPanelOpen: false,
  setVersionsPanelOpen: (v) => set({ versionsPanelOpen: v }),
  uploadModalOpen: false,
  setUploadModalOpen: (v) => set({ uploadModalOpen: v }),

  // When set, UploadModal opens at the review step for this version set's
  // sheets — lets users resume a draft from the Version Sets panel.
  resumeDraftVersionSetId: null,
  openWizardForDraft: (id) => set({ resumeDraftVersionSetId: id, uploadModalOpen: true }),
  clearResumeDraft: () => set({ resumeDraftVersionSetId: null }),

  createProjectModalOpen: false,
  setCreateProjectModalOpen: (v) => set({ createProjectModalOpen: v }),

  // ── Compare mode ──
  // Setup modal for picking which two issuances + colors to overlay.
  compareSetupOpen: false,
  setCompareSetupOpen: (v) => set({ compareSetupOpen: v }),

  // When non-null, ProjectDrawings swaps the regular PdfViewer for the
  // CompareViewer (overlaid render of two sheets). Cleared on Exit Compare.
  // Shape: { sheetA_id, sheetB_id, colorA_id, colorB_id, sheet_number }.
  compareConfig: null,
  setCompareConfig: (cfg) => set({ compareConfig: cfg }),
  exitCompare: () => set({ compareConfig: null }),

  // ── Viewer ──
  zoom: 1.0,
  setZoom: (z) => set({ zoom: Math.min(Math.max(z, 0.1), 10) }),
  pan: { x: 0, y: 0 },
  setPan: (pan) => set({ pan }),

  // ── Markups ──
  markups: [],
  setMarkups: (markups) => set({ markups }),
  addMarkup: (markup) => set((s) => ({ markups: [...s.markups, markup] })),
  removeMarkup: (id) => set((s) => ({ markups: s.markups.filter(m => m.id !== id) })),
  updateMarkup: (id, patch) => set((s) => ({
    markups: s.markups.map(m => m.id === id ? { ...m, ...patch } : m),
  })),

  // ── Markup clipboard ──
  // Holds copied markup payloads (id-stripped, sheet-stripped) for
  // Ctrl/Cmd+C → Ctrl/Cmd+V across sheets within the session. Only
  // unpublished markups are eligible to copy. Pasting clones each
  // entry into the active sheet at a small visual offset.
  markupClipboard: [],
  setMarkupClipboard: (clipboard) => set({ markupClipboard: clipboard }),

  // ── Markup tool state ──
  // pan | arrow | cloud | rect | line | text | freehand | measure (legacy) |
  // calibrate | measure-line | measure-polyline | measure-freeline |
  // measure-rect-area | measure-polygon-area | measure-freeform-area |
  // measure-rect-volume | measure-polygon-volume | measure-freeform-volume
  activeTool: 'pan',
  activeColor: '#e8a020',
  setActiveTool: (t) => set({ activeTool: t }),
  setActiveColor: (c) => set({ activeColor: c }),
  activeFillColor: 'transparent',
  setActiveFillColor: (c) => set({ activeFillColor: c }),
  activeFillOpacity: 1,
  setActiveFillOpacity: (o) => set({ activeFillOpacity: Math.min(1, Math.max(0, o)) }),

  // ── Calibration picking state (2-point workflow) ──
  // While activeTool === 'calibrate', pointer clicks in MarkupCanvas push here.
  // Reaching 2 points triggers the entry modal to open automatically.
  calibrationPickPoints: [],
  addCalibrationPickPoint: (pt) => set((s) => {
    const next = [...s.calibrationPickPoints, pt].slice(-2);
    if (next.length === 2) {
      return {
        calibrationPickPoints: next,
        calibrationModalOpen: true,
        activeTool: 'pan',
      };
    }
    return { calibrationPickPoints: next };
  }),
  resetCalibrationPick: () => set({ calibrationPickPoints: [] }),

  // Calibration modal open flag
  calibrationModalOpen: false,
  setCalibrationModalOpen: (v) => set({ calibrationModalOpen: v }),

  // ── Content search ──
  contentSearchOpen: false,
  setContentSearchOpen: (v) => set({ contentSearchOpen: v }),

  // ── Drawings list selection ──
  selectedSheets: new Set(),
  setSelectedSheets: (s) => set({ selectedSheets: s }),
  toggleSheetSelection: (id) => set((state) => {
    const next = new Set(state.selectedSheets);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { selectedSheets: next };
  }),
  clearSheetSelection: () => set({ selectedSheets: new Set() }),

  // ── Connectivity ──
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  setIsOnline: (v) => set({ isOnline: v }),

  // ── Drawings sub-panels ──
  drawingsSettingsOpen: false,
  setDrawingsSettingsOpen: (v) => set({ drawingsSettingsOpen: v }),
  // Combined VERSION SETS modal — replaces the standalone DELETED + PUBLISH
  // LOG modals. `versionSetsModalTab` is optional and lets callers deep-link
  // straight to a tab (null = default to last-picked / publish).
  versionSetsModalOpen: false,
  setVersionSetsModalOpen: (v) => set({ versionSetsModalOpen: v }),
  versionSetsModalTab: null, // null | 'publish' | 'deleted'
  setVersionSetsModalTab: (t) => set({ versionSetsModalTab: t }),

}));
